import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllDocuments, createDocument, getAllEngagements } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import { Upload, FileText, Image, Eye } from 'lucide-react';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import DocumentForm from '../../components/forms/DocumentForm';
import { DOCUMENT_CATEGORY, DOCUMENT_VISIBILITY, formatDate, label, options } from '../../constants/garmentCodes';

const IMAGE_TYPES = ['PNG', 'JPG', 'JPEG', 'WEBP'];
const typeIcon = (type) => (IMAGE_TYPES.includes(String(type || '').toUpperCase()) ? <Image size={14} className="text-[#3D52A0]" /> : <FileText size={14} className="text-[#92560C]" />);
const typeBg = (type) => {
  const t = String(type || '').toUpperCase();
  if (IMAGE_TYPES.includes(t)) return 'bg-[#EEF0FB]';
  if (t === 'PDF') return 'bg-[#FEF3E2]';
  if (t === 'XLSX') return 'bg-[#E8F5EE]';
  return 'bg-[#F4F3F0]';
};

export default function BrandDocuments() {
  const dispatch = useDispatch();
  const documents = useSelector((state) => state.document.documents);
  const loading = useSelector((state) => state.document.loading);
  const engagements = useSelector((state) => state.engagement.engagements);
  const userId = useSelector((state) => state.auth?.loginUser?.userId ?? null);
  const brandId = useSelector((state) => state.brand.profile?.id);
  const [category, setCategory] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllEngagements({ brandId }));
  }, [dispatch, brandId]);

  useEffect(() => {
    dispatch(getAllDocuments({ brandId, category }));
  }, [dispatch, brandId, category]);

  const create = (body) => {
    dispatch(createDocument({ payload: body }));
    setUploadOpen(false);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title="Documents" subtitle={`${documents.length} files`} actions={<Button variant="primary" size="sm" icon={<Upload size={14} />} onClick={() => setUploadOpen(true)}>Add document</Button>} />
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="flex gap-2 flex-wrap items-center">
          <button onClick={() => setCategory('')} className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${category === '' ? 'bg-[#1E2A3B] text-white border-transparent' : 'bg-white text-[#5C5A54] border-[#E5E3DE] hover:bg-[#F4F3F0]'}`}>All documents</button>
          {options(DOCUMENT_CATEGORY).map((o) => (
            <button key={o.value} onClick={() => setCategory(o.value)} className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${category === o.value ? 'bg-[#1E2A3B] text-white border-transparent' : 'bg-white text-[#5C5A54] border-[#E5E3DE] hover:bg-[#F4F3F0]'}`}>
              {o.label}
            </button>
          ))}
          {loading && <span className="text-xs text-[#9B9893]">Loading…</span>}
        </div>

        <div className="bg-white rounded-xl border border-[#E5E3DE] overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="BrandDocuments"
            columns={[
              { id: 'type', label: 'TYPE', align: 'left', width: 60 },
              { id: 'fileName', label: 'FILE NAME', align: 'left' },
              { id: 'category', label: 'CATEGORY', align: 'left' },
              { id: 'linked', label: 'LINKED TO', align: 'left' },
              { id: 'uploadedBy', label: 'UPLOADED BY', align: 'left' },
              { id: 'date', label: 'DATE', align: 'left' },
              { id: 'size', label: 'SIZE', align: 'left' },
              { id: 'visibility', label: 'VISIBILITY', align: 'left' },
              { id: 'action', label: '', align: 'center', disableFilter: true },
            ]}
            rows={documents.map((doc) => ({
              id: doc.id,
              rColumns: [
                <div key="type" label={doc.fileType || ''} className="flex items-center w-full h-full px-2 py-1">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${typeBg(doc.fileType)}`}>{typeIcon(doc.fileType)}</div>
                </div>,
                <div key="name" label={doc.name || ''} className={`flex flex-col justify-center w-full h-full px-2 py-1 ${doc.__optimistic ? 'opacity-60' : ''}`}>
                  <p className="text-sm font-medium text-[#1A1915] truncate">{doc.name}</p>
                  <p className="text-[10px] text-[#9B9893] uppercase">{doc.fileType || ''}</p>
                </div>,
                <div key="cat" label={label(DOCUMENT_CATEGORY, doc.category)} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{label(DOCUMENT_CATEGORY, doc.category)}</div>,
                <div key="link" label={doc.linkedItemType || ''} className="flex items-center w-full h-full px-2 py-1 font-mono text-xs text-[#3D52A0]">{doc.linkedItemType ? `${doc.linkedItemType} #${doc.linkedItemId ?? ''}` : '—'}</div>,
                <div key="by" label={doc.uploadedBy || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{doc.uploadedBy || '—'}</div>,
                <div key="date" label={doc.uploadedDate || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#9B9893]">{formatDate(doc.uploadedDate)}</div>,
                <div key="size" label={doc.size || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#9B9893]">{doc.size || '—'}</div>,
                <div key="vis" label={label(DOCUMENT_VISIBILITY, doc.visibility)} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{label(DOCUMENT_VISIBILITY, doc.visibility)}</div>,
                <div key="act" label="" className="flex items-center justify-center w-full h-full px-2 py-1">
                  {doc.fileUrl && (
                    <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-[#F4F3F0] text-[#9B9893] hover:text-[#3D52A0]" title="Open">
                      <Eye size={14} />
                    </a>
                  )}
                </div>,
              ],
            }))}
          />
          {!loading && documents.length === 0 && <p className="py-10 text-center text-sm text-[#9B9893]">No documents</p>}
        </div>
      </div>

      <Modal open={uploadOpen} onClose={() => setUploadOpen(false)} title="Add Document" size="md">
        <DocumentForm engagements={engagements} userId={userId} onSubmit={create} onCancel={() => setUploadOpen(false)} />
      </Modal>
    </div>
  );
}
